/**
 * React Query hooks for the Org Brain BFF endpoints.
 *
 * Closes part of gaps B8 + H1. The BFF controller owns role-guarding;
 * these hooks are just typed fetchers.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient as api } from '@services/api-client';

export interface OrgBrainConfig {
  tenant_id: string;
  is_enabled: boolean;
  enabled_at: string | null;
  enabled_by_user_id: string | null;
  runtime_retention_days: number;
  consent_version: string | null;
  consented_at: string | null;
  key_epoch: number;
  // D2 dual-flag: when `auto_enabled = true` and `acknowledged_at = null`
  // the Settings UI shows an acknowledgement banner instead of the
  // normal "Enabled" state.
  auto_enabled?: boolean;
  acknowledged_at?: string | null;
  // D4: surfaces explicit-disable history to the UI so admins can see
  // why an auto-enable might have been skipped (currently informational).
  disabled_by_user_at?: string | null;
}

export interface AskMatch {
  node_id: string;
  node_type: string;
  natural_key: string;
  similarity: number;
  text: string;
}

export interface AskGraphNode {
  id: string;
  type: string;
  natural_key: string;
}
export interface AskGraphEdge {
  type: string;
  from_node_id: string;
  to_node_id: string;
}
export interface AskGraph {
  nodes: AskGraphNode[];
  edges: AskGraphEdge[];
}

export interface AskResponse {
  query: string;
  cited_node_ids: string[];
  serialized_context: string;
  matches: AskMatch[];
  diagnostics: Record<string, unknown>;
  latency_ms: number;
  // LLM-synthesized prose answer (null when no key configured or no
  // confident match). `grounded` = backed by confident matches.
  answer?: string | null;
  grounded?: boolean;
  // The cited subgraph, so the UI can draw the answer's grounding as a
  // knowledge graph (nodes + typed relationships).
  graph?: AskGraph;
}

export interface ImpactFinding {
  severity: string;
  category: string;
  message: string;
  affected_nodes: { id: string; type: string; natural_key: string }[];
}

export interface ImpactReport {
  kind: string;
  severity: string;
  summary: string;
  findings: ImpactFinding[];
  suggestions: string[];
  diagnostics: Record<string, number>;
  latency_ms: number;
}

export interface OrgBrainStats {
  tenant_id: string;
  enrolled: boolean;
  enabled_at: string | null;
  consent_version: string | null;
  runtime_retention_days: number | null;
  node_count: number;
  edge_count: number;
  last_indexed_at: string | null;
  last_drift_scan_at: string | null;
  last_drift_severity: 'info' | 'warning' | 'breaking' | null;
  drift_findings_count: number;
  last_query_at: string | null;
  queries_last_24h: number;
  kafka_consumer_healthy: boolean | null;
  computed_at: string;
}

// apiClient's baseURL is already `/api` (the BFF), and the BFF mounts this
// controller at `/api/org-brain` (global prefix `api` + @Controller('org-brain')).
// So paths here must be BFF-relative — NOT `/api/v1/org-brain`, which would
// double the prefix to `/api/api/v1/...` and 404.
const BASE = '/org-brain';

export function useOrgBrainConfig(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: ['orgBrainConfig', tenantId],
    queryFn: async (): Promise<OrgBrainConfig> =>
      (await api.get(`${BASE}/tenants/${tenantId}/config`)).data,
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });
}

export function useEnableOrgBrain(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      consent_version: string;
      runtime_retention_days?: number;
    }): Promise<OrgBrainConfig> =>
      (await api.post(`${BASE}/tenants/${tenantId}/enable`, body)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['orgBrainConfig', tenantId] }),
  });
}

export function useDisableOrgBrain(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<OrgBrainConfig> =>
      (await api.post(`${BASE}/tenants/${tenantId}/disable`, {})).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['orgBrainConfig', tenantId] }),
  });
}

/**
 * D2 dual-flag — POST acknowledgement of the auto-enable banner.
 * The `consent_version` is the real ToS string the admin agreed to;
 * the BFF forwards it to the ai-engine, which overwrites the
 * platform-generated `auto-on-upgrade-pro-v1` marker.
 */
export function useAcknowledgeOrgBrain(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      consent_version: string;
    }): Promise<OrgBrainConfig> =>
      (await api.post(`${BASE}/tenants/${tenantId}/acknowledge`, body)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['orgBrainConfig', tenantId] }),
  });
}

export interface ReindexResult {
  scraped: number;
  sources: Record<string, number>;
  bootstrap: { total_events?: number; passes?: number } & Record<string, unknown>;
}

/**
 * Self-service ingestion — scrape the tenant's config from the source
 * services and bootstrap the graph. Invalidates the stats query so the
 * Settings widget's node/edge counts refresh once ingestion completes.
 */
export function useReindexWorkspace(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ReindexResult> =>
      (await api.post(`${BASE}/tenants/${tenantId}/reindex`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orgBrainStats', tenantId] });
    },
  });
}

export function useAskOrgBrain(tenantId: string) {
  return useMutation({
    mutationFn: async (body: {
      query: string;
      top_k?: number;
      max_hops?: number;
      min_similarity?: number;
      node_types?: string[];
    }): Promise<AskResponse> =>
      (await api.post(`${BASE}/tenants/${tenantId}/ask`, body)).data,
  });
}

export function useSimulateChange(tenantId: string) {
  return useMutation({
    mutationFn: async (body: Record<string, unknown>): Promise<ImpactReport> =>
      (await api.post(`${BASE}/tenants/${tenantId}/simulate`, body)).data,
  });
}

// -------- Config health / drift (B3-b) --------

export interface HealthFinding {
  check: string;
  severity: 'info' | 'warning' | 'breaking' | string;
  message: string;
  affected_nodes: { id: string; type: string; natural_key: string; source_id?: string | null }[];
  suggested_fix?: string | null;
}

export interface HealthReport {
  overall_severity: 'info' | 'warning' | 'breaking' | string;
  findings: HealthFinding[];
  checks_run: string[];
  checks_failed: string[];
  duration_ms: number;
}

export function useOrgBrainHealth(tenantId: string) {
  return useMutation({
    mutationFn: async (): Promise<HealthReport> =>
      (await api.post(`${BASE}/tenants/${tenantId}/health`, {})).data,
  });
}

// -------- Learning insights (B3-c) --------

export interface LearningInsight {
  kind: string;
  severity: 'info' | 'warning' | 'breaking' | string;
  message: string;
  affected_nodes?: { id: string; type: string; natural_key: string }[];
  metrics?: Record<string, number>;
}

export interface InsightReport {
  insights: LearningInsight[];
  generated_at?: string;
}

export function useOrgBrainInsights(tenantId: string) {
  return useMutation({
    mutationFn: async (): Promise<InsightReport> =>
      (await api.post(`${BASE}/tenants/${tenantId}/learning/insights`, {})).data,
  });
}

// -------- Extension API keys (B3-d) --------

export interface OrgBrainApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  created_at: string;
  revoked_at?: string | null;
  superseded_at?: string | null;
}

export interface IssuedApiKey extends OrgBrainApiKey {
  // Plaintext is returned ONCE on issue/rotate and never stored.
  plaintext_key?: string;
  api_key?: string;
}

export function useOrgBrainApiKeys(
  tenantId: string | null | undefined,
  includeRevoked = false,
) {
  return useQuery({
    queryKey: ['orgBrainApiKeys', tenantId, includeRevoked],
    queryFn: async (): Promise<OrgBrainApiKey[]> => {
      const q = includeRevoked ? '?include_revoked=true' : '';
      const data = (await api.get(`${BASE}/tenants/${tenantId}/api-keys${q}`)).data;
      return Array.isArray(data) ? data : (data?.keys ?? data?.items ?? []);
    },
    enabled: Boolean(tenantId),
  });
}

export function useIssueApiKey(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      scopes: string[];
      description?: string;
    }): Promise<IssuedApiKey> =>
      (await api.post(`${BASE}/tenants/${tenantId}/api-keys`, body)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['orgBrainApiKeys', tenantId] }),
  });
}

export function useRevokeApiKey(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (apiKeyId: string): Promise<{ ok: boolean }> =>
      (await api.delete(`${BASE}/tenants/${tenantId}/api-keys/${apiKeyId}`)).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['orgBrainApiKeys', tenantId] }),
  });
}

export function useRotateApiKey(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      apiKeyId: string;
      grace_seconds?: number;
    }): Promise<IssuedApiKey> =>
      (
        await api.post(
          `${BASE}/tenants/${tenantId}/api-keys/${vars.apiKeyId}/rotate`,
          { grace_seconds: vars.grace_seconds },
        )
      ).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['orgBrainApiKeys', tenantId] }),
  });
}

/**
 * B2 — 30s-polling status snapshot for the Settings Org Brain card.
 * Backend caches for 15s, so the refetch cost stays flat as more admins
 * look at the page.
 */
export function useOrgBrainStats(
  tenantId: string | null | undefined,
  options?: { enabled?: boolean; refetchIntervalMs?: number },
) {
  const enabled = Boolean(tenantId) && (options?.enabled ?? true);
  return useQuery({
    queryKey: ['orgBrainStats', tenantId],
    queryFn: async (): Promise<OrgBrainStats> =>
      (await api.get(`${BASE}/tenants/${tenantId}/stats`)).data,
    enabled,
    staleTime: 15_000,
    refetchInterval: options?.refetchIntervalMs ?? 30_000,
    refetchOnWindowFocus: false,
  });
}
